import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Trophy, Heart, MessageCircle, Share2, Twitter, Copy, Check, MessageSquare,
  Coins, Users, Swords, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { api, resolveAsset, timeAgo } from "../lib/api";
import { useLiveWS } from "../hooks/useLiveWS";
import { useAuth } from "../lib/auth";

/**
 * Full-width Play-of-the-Week showcase for the homepage.
 *
 * Replaces the static stats grid with the big featured clip + a
 * slim row of compact stat chips underneath. If no clip is featured,
 * falls back to the normal stats grid so the homepage never feels empty.
 */
export default function PlayOfTheWeekShowcase({ stats }) {
  const { user } = useAuth();
  const [clip, setClip] = useState(null);
  const [likeBusy, setLikeBusy] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/clips/featured");
      setClip(data && data.featured ? data : null);
    } catch {
      setClip(null);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useLiveWS((msg) => {
    if (
      msg.event === "clip_featured" ||
      msg.event === "clip_deleted" ||
      msg.event === "clip_liked" ||
      msg.event === "clip_unliked"
    ) {
      load();
    }
  });

  const shareLinks = useMemo(() => {
    if (!clip || typeof window === "undefined") return null;
    const origin = window.location.origin;
    const shareUrl = `${origin}/play-of-the-week?clip=${clip.id}`;
    const text = `🎮 ${clip.title} · Play of the Week on Spicy Community`;
    return {
      shareUrl,
      text,
      tweet: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`,
      discord: `${text}\n${shareUrl}`,
    };
  }, [clip]);

  const toggleLike = async () => {
    if (!user) {
      toast.error("Sign in to like clips");
      return;
    }
    if (!clip || likeBusy) return;
    setLikeBusy(true);
    const prev = clip;
    setClip({
      ...clip,
      viewer_has_liked: !clip.viewer_has_liked,
      like_count: (clip.like_count || 0) + (clip.viewer_has_liked ? -1 : 1),
    });
    try {
      if (prev.viewer_has_liked) {
        await api.delete(`/clips/${prev.id}/like`);
      } else {
        await api.post(`/clips/${prev.id}/like`);
      }
    } catch {
      setClip(prev);
      toast.error("Could not update like");
    } finally {
      setLikeBusy(false);
    }
  };

  const copyLink = async () => {
    if (!shareLinks) return;
    try {
      await navigator.clipboard.writeText(shareLinks.shareUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed");
    }
  };

  const copyDiscord = async () => {
    if (!shareLinks) return;
    try {
      await navigator.clipboard.writeText(shareLinks.discord);
      toast.success("Discord snippet copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const nativeShare = async () => {
    if (!shareLinks) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: clip.title, text: shareLinks.text, url: shareLinks.shareUrl });
      } catch { /* cancelled */ }
    } else {
      copyLink();
    }
  };

  // ---------- Compact stats chips (always visible below) ----------
  const statsRow = (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
      {[
        { label: "Prize Pool", value: `$${(stats?.prize || 0).toLocaleString()}`, Icon: Coins },
        { label: "Teams", value: stats?.teams || 0, Icon: Users },
        { label: "Matches", value: stats?.matches || 0, Icon: Swords },
        { label: "Live Now", value: stats?.live ? "ON AIR" : "—", live: stats?.live, Icon: Trophy },
      ].map((s, i) => (
        <div
          key={i}
          data-testid={`home-stat-${i}`}
          className="glass-dark rounded-xl px-4 py-3 flex items-center gap-3 relative overflow-hidden"
        >
          <s.Icon size={14} className="text-red-400/80 shrink-0" />
          <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-mono leading-none">{s.label}</div>
            <div className="font-display font-bold text-base md:text-lg leading-tight mt-0.5 flex items-center gap-1.5">
              {s.value}
              {s.live && <span className="live-dot" />}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  if (!clip) {
    // Fallback — just the stats block, but in the new compact chip format so
    // the homepage layout stays consistent.
    return (
      <section className="max-w-7xl mx-auto px-6 mt-10" data-testid="home-stats-only">
        {statsRow}
      </section>
    );
  }

  const thumb = clip.thumb_url ? resolveAsset(clip.thumb_url) : null;
  const isYouTube = clip.source_type === "youtube" && clip.youtube_id;

  return (
    <AnimatePresence mode="wait">
      <motion.section
        key={clip.id}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-7xl mx-auto px-6 mt-10"
        data-testid="home-potw-showcase"
      >
        <div className="relative overflow-hidden rounded-3xl border border-red-500/25 bg-gradient-to-br from-red-950/30 via-black/70 to-black shadow-[0_0_40px_rgba(230,0,0,0.15)]">
          {/* Faint grain */}
          <div className="pointer-events-none absolute inset-0 opacity-[0.04] bg-[repeating-linear-gradient(0deg,rgba(255,255,255,0.5)_0px,rgba(255,255,255,0.5)_1px,transparent_1px,transparent_3px)]" />

          <div className="relative grid md:grid-cols-[1.35fr_1fr] gap-0">
            {/* Video / iframe */}
            <div className="relative aspect-video bg-black md:rounded-r-none">
              {isYouTube ? (
                <iframe
                  src={`https://www.youtube.com/embed/${clip.youtube_id}?rel=0&modestbranding=1`}
                  title={clip.title}
                  data-testid="home-potw-iframe"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="w-full h-full border-0"
                />
              ) : (
                <video
                  controls
                  playsInline
                  preload="metadata"
                  src={resolveAsset(clip.video_url)}
                  poster={thumb || undefined}
                  data-testid="home-potw-video"
                  className="w-full h-full object-contain bg-black"
                />
              )}
              {/* YT badge */}
              {isYouTube && (
                <span className="absolute top-3 left-3 inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.25em] px-2 py-0.5 rounded-full bg-red-600/90 border border-red-400/60 text-white">
                  YouTube
                </span>
              )}
            </div>

            {/* Metadata panel */}
            <div className="p-6 md:p-8 flex flex-col justify-center">
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-yellow-300 mb-3">
                <Trophy size={12} fill="currentColor" />
                Play of the Week
                <span className="text-zinc-500 normal-case tracking-widest">· {timeAgo(clip.created_at)}</span>
              </div>

              <h3 className="font-display font-black text-2xl md:text-3xl lg:text-4xl tracking-tight text-white leading-[1.05] mb-3">
                {clip.title}
              </h3>

              {clip.description && (
                <p className="text-sm text-zinc-400 mb-5 line-clamp-3">{clip.description}</p>
              )}

              {/* Actions */}
              <div className="flex items-center flex-wrap gap-2 mb-5">
                <button
                  onClick={toggleLike}
                  disabled={likeBusy}
                  data-testid="home-potw-like"
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full border transition-all ${
                    clip.viewer_has_liked
                      ? "bg-red-500/20 border-red-500/50 text-red-200"
                      : "glass-dark text-zinc-300 hover:border-red-500/40"
                  }`}
                >
                  <Heart size={13} fill={clip.viewer_has_liked ? "currentColor" : "none"} />
                  <span className="font-mono font-bold text-xs">{clip.like_count || 0}</span>
                </button>

                <Link
                  to={`/play-of-the-week?clip=${clip.id}#comments`}
                  data-testid="home-potw-comments"
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full border glass-dark text-zinc-300 hover:border-red-500/40 transition-all"
                >
                  <MessageCircle size={13} />
                  <span className="font-mono font-bold text-xs">{clip.comment_count || 0}</span>
                </Link>

                <div className="relative">
                  <button
                    onClick={() => setShareOpen((v) => !v)}
                    data-testid="home-potw-share"
                    aria-expanded={shareOpen}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full border transition-all ${
                      shareOpen
                        ? "bg-red-500/20 border-red-500/50 text-red-200"
                        : "glass-dark text-zinc-300 hover:border-red-500/40"
                    }`}
                  >
                    <Share2 size={13} />
                    <span className="font-mono font-bold text-xs">Share</span>
                  </button>
                  {shareOpen && shareLinks && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setShareOpen(false)}
                        aria-hidden
                      />
                      <div
                        data-testid="home-potw-share-popover"
                        className="fixed inset-x-4 bottom-4 z-50 md:absolute md:inset-auto md:right-0 md:top-full md:mt-2 md:w-72 glass-red-glow rounded-2xl p-4 shadow-2xl"
                      >
                        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-red-300 mb-2">
                          Share this clip
                        </div>
                        <div className="flex items-stretch gap-2 mb-3">
                          <input
                            readOnly
                            value={shareLinks.shareUrl}
                            onFocus={(e) => e.target.select()}
                            className="flex-1 min-w-0 bg-black/60 border border-white/10 rounded-lg px-2.5 text-[11px] text-zinc-200 focus:outline-none focus:border-red-500 font-mono"
                          />
                          <button
                            onClick={copyLink}
                            aria-label="Copy link"
                            className="btn-liquid !py-2 !px-3 !text-[11px]"
                          >
                            {copied ? <Check size={12} /> : <Copy size={12} />}
                          </button>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <a
                            href={shareLinks.tweet}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() => setShareOpen(false)}
                            className="btn-ghost-glass !text-[10px] !py-2 justify-center !gap-1"
                          >
                            <Twitter size={12} /> X
                          </a>
                          <button
                            onClick={copyDiscord}
                            className="btn-ghost-glass !text-[10px] !py-2 justify-center !gap-1"
                          >
                            <MessageSquare size={12} /> Discord
                          </button>
                          <button
                            onClick={nativeShare}
                            className="btn-ghost-glass !text-[10px] !py-2 justify-center"
                          >
                            More…
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <Link
                to={`/play-of-the-week?clip=${clip.id}`}
                data-testid="home-potw-watch-full"
                className="btn-liquid self-start !py-2.5 !px-5 !text-xs"
              >
                Watch full <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        </div>

        {/* Compact stats row — always present */}
        {statsRow}
      </motion.section>
    </AnimatePresence>
  );
}
